// src/pages/customer-panel/live-conversations/LiveConversationsPage.js
// Live Chat Agent Interface - Split layout with session sidebar and active chat panel

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemButton,
    Avatar,
    TextField,
    IconButton,
    Badge,
    Chip,
    Tabs,
    Tab,
    Divider,
    CircularProgress,
    Alert,
    Tooltip,
    InputAdornment,
    Snackbar,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import axiosInstance from '../../../utils/axiosInstance';
import { liveChatSocket } from '../../../utils/LiveChatSocket';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#10b981';
const ACCENT_DARK = '#059669';

// --- Simple Markdown to HTML converter for bot messages ---
const convertMarkdownToHtml = (text) => {
    if (!text) return '';
    let html = text;
    html = html.replace(/^######\s+(.*$)/gim, '<h6 style="margin:4px 0;font-size:0.85em;font-weight:700;">$1</h6>');
    html = html.replace(/^#####\s+(.*$)/gim, '<h5 style="margin:4px 0;font-size:0.9em;font-weight:700;">$1</h5>');
    html = html.replace(/^####\s+(.*$)/gim, '<h4 style="margin:4px 0;font-size:0.95em;font-weight:700;">$1</h4>');
    html = html.replace(/^###\s+(.*$)/gim, '<h3 style="margin:6px 0;font-size:1em;font-weight:700;">$1</h3>');
    html = html.replace(/^##\s+(.*$)/gim, '<h2 style="margin:6px 0;font-size:1.05em;font-weight:700;">$1</h2>');
    html = html.replace(/^#\s+(.*$)/gim, '<h1 style="margin:6px 0;font-size:1.1em;font-weight:700;">$1</h1>');
    html = html.replace(/\*\*([^*]+)\*\*/gim, '<strong>$1</strong>');
    html = html.replace(/__([^_]+)__/gim, '<strong>$1</strong>');
    html = html.replace(/\*([^*]+)\*/gim, '<em>$1</em>');
    html = html.replace(/_([^_]+)_/gim, '<em>$1</em>');
    html = html.replace(/```([^`]+)```/gim, '<pre style="background:#f1f5f9;padding:8px;border-radius:6px;font-size:0.85em;overflow-x:auto;"><code>$1</code></pre>');
    html = html.replace(/`([^`]+)`/gim, '<code style="background:#f1f5f9;padding:1px 4px;border-radius:3px;font-size:0.9em;">$1</code>');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#3b82f6;text-decoration:underline;">$1</a>');
    html = html.replace(/^\* (.+)$/gim, '<li style="margin:2px 0;">$1</li>');
    html = html.replace(/^- (.+)$/gim, '<li style="margin:2px 0;">$1</li>');
    html = html.replace(/^\d+\. (.+)$/gim, '<li style="margin:2px 0;">$1</li>');
    html = html.replace(/\n\n/g, '<br/><br/>');
    html = html.replace(/\n/g, '<br/>');
    return html;
};

const STATUS_CONFIG = {
    queued: { label: 'Queued', color: 'warning' },
    active: { label: 'Active', color: 'success' },
    resolved: { label: 'Resolved', color: 'info' },
    closed: { label: 'Closed', color: 'default' },
};

const FILTER_TABS = ['All', 'Queued', 'Active', 'Resolved'];

export default function LiveConversationsPage() {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    // State
    const [sessions, setSessions] = useState([]);
    const [filteredSessions, setFilteredSessions] = useState([]);
    const [activeSession, setActiveSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState('');
    const [filterTab, setFilterTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [unreadCounts, setUnreadCounts] = useState({});

    // Agent status
    const [agentStatus, setAgentStatus] = useState(null); // 'online'|'offline'|'away'|'busy'|null
    const [statusLoading, setStatusLoading] = useState(false);

    const messagesEndRef = useRef(null);
    const pollIntervalRef = useRef(null);
    const lastMessageCountRef = useRef({});
    const wsCallbackRef = useRef(null);
    const wsSessionRef = useRef(null);

    // Fetch agent status from TeamMembership
    const fetchAgentStatus = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/live-chat/agent/status');
            const data = response.data?.data || response.data || {};
            setAgentStatus((data.status || 'offline').toLowerCase());
        } catch {
            setAgentStatus('offline');
        }
    }, []);

    // Toggle agent online/offline
    const handleToggleStatus = async () => {
        const newStatus = agentStatus === 'online' ? 'offline' : 'online';
        try {
            setStatusLoading(true);
            await axiosInstance.put('/live-chat/agent/status', { status: newStatus });
            setAgentStatus(newStatus);
            setSnackbar({ open: true, message: `Status changed to ${newStatus}`, severity: 'success' });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to update status',
                severity: 'error',
            });
        } finally {
            setStatusLoading(false);
        }
    };

    // Fetch sessions
    const fetchSessions = useCallback(async () => {
        try {
            const statusFilter = FILTER_TABS[filterTab].toLowerCase();
            const params = {};
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            if (searchQuery.trim()) {
                params.search = searchQuery.trim();
            }
            const response = await axiosInstance.get('/live-chat/sessions', { params });
            const data = response.data?.data || response.data?.sessions || [];
            const sessionList = Array.isArray(data) ? data : [];
            setSessions(sessionList);
            // Track unread counts based on message_count changes
            setUnreadCounts((prev) => {
                const updated = { ...prev };
                sessionList.forEach((s) => {
                    const sid = s.session_id;
                    const msgCount = s.message_count || 0;
                    const lastKnown = lastMessageCountRef.current[sid] || 0;
                    if (activeSession?.session_id === sid) {
                        // Currently viewing - no unreads
                        updated[sid] = 0;
                        lastMessageCountRef.current[sid] = msgCount;
                    } else if (lastKnown > 0 && msgCount > lastKnown) {
                        updated[sid] = (updated[sid] || 0) + (msgCount - lastKnown);
                        lastMessageCountRef.current[sid] = msgCount;
                    } else if (!lastMessageCountRef.current[sid]) {
                        lastMessageCountRef.current[sid] = msgCount;
                    }
                });
                return updated;
            });
            setError(null);
        } catch (err) {
            console.error('Error fetching sessions:', err);
            if (!sessions.length) {
                setError(err.response?.data?.message || 'Failed to load sessions');
            }
        } finally {
            setLoading(false);
        }
    }, [filterTab, searchQuery]);

    // Fetch messages for active session
    const fetchMessages = useCallback(async (sessionId) => {
        if (!sessionId) return;
        try {
            setMessagesLoading(true);
            const response = await axiosInstance.get(`/live-chat/sessions/${sessionId}`);
            const sessionData = response.data?.data || {};
            const conv = sessionData.conversation || [];
            const agentId = sessionData.agent?.id;
            const transformed = conv.map((msg, idx) => {
                let sender_type = 'customer';
                if (msg.type === 'bot_history' || msg.sender_id === 'bot') {
                    sender_type = 'bot';
                } else if (msg.sender_type === 'system' || msg.type === 'system') {
                    sender_type = 'system';
                } else if (msg.sender_id === agentId || msg.sender_type === 'agent') {
                    sender_type = 'agent';
                }
                return {
                    message_id: msg.id || `msg-${idx}`,
                    sender_type,
                    sender_name: msg.sender_name || '',
                    message: msg.message || msg.content || '',
                    created_at: msg.timestamp || msg.created_at,
                };
            });
            setMessages(transformed);
            if (sessionData.status) {
                setActiveSession(prev => prev ? { ...prev, status: sessionData.status } : prev);
            }
        } catch (err) {
            console.error('Error fetching messages:', err);
            setSnackbar({ open: true, message: 'Failed to load messages', severity: 'error' });
        } finally {
            setMessagesLoading(false);
        }
    }, []);

    // Fetch agent status on mount
    useEffect(() => {
        fetchAgentStatus();
    }, [fetchAgentStatus]);

    // Initial load and polling
    useEffect(() => {
        fetchSessions();
        pollIntervalRef.current = setInterval(() => {
            fetchSessions();
        }, 10000);
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, [fetchSessions]);

    // Cleanup WebSocket on unmount
    useEffect(() => {
        return () => {
            if (wsSessionRef.current && wsCallbackRef.current) {
                liveChatSocket.unsubscribe(wsSessionRef.current, wsCallbackRef.current);
            }
        };
    }, []);

    // Filter sessions client-side
    useEffect(() => {
        let result = [...sessions];
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(
                (s) =>
                    (s.customer_name || '').toLowerCase().includes(q) ||
                    (s.customer_email || '').toLowerCase().includes(q) ||
                    String(s.session_id || '').toLowerCase().includes(q)
            );
        }
        setFilteredSessions(result);
    }, [sessions, searchQuery]);

    // Scroll to bottom on new messages
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Connect WebSocket for a session (returns promise so callers can await)
    const connectWebSocket = useCallback(async (sessionId) => {
        // Disconnect previous session if any
        if (wsSessionRef.current && wsCallbackRef.current) {
            liveChatSocket.unsubscribe(wsSessionRef.current, wsCallbackRef.current);
        }

        const onWsMessage = (data) => {
            if (data.type === 'message' && data.sender_id !== user?.user_id) {
                // Incoming message from customer or other agent
                setMessages((prev) => [
                    ...prev,
                    {
                        message_id: `ws-${Date.now()}-${Math.random()}`,
                        sender_type: 'customer',
                        sender_name: data.sender_name || '',
                        message: data.message || '',
                        created_at: data.timestamp || new Date().toISOString(),
                    },
                ]);
            } else if (data.type === 'system') {
                setMessages((prev) => [
                    ...prev,
                    {
                        message_id: `sys-${Date.now()}`,
                        sender_type: 'system',
                        sender_name: '',
                        message: data.message || data.event || 'System event',
                        created_at: data.timestamp || new Date().toISOString(),
                    },
                ]);
                // Refresh sessions if session resolved
                if (data.message === 'session_auto_resolved' || data.message === 'agent_resolved_chat') {
                    fetchSessions();
                }
            }
        };

        wsCallbackRef.current = onWsMessage;
        wsSessionRef.current = sessionId;
        await liveChatSocket.subscribe(sessionId, user?.user_id, onWsMessage);
    }, [user?.user_id, fetchSessions]);

    // Select a session
    const handleSelectSession = (session) => {
        setActiveSession(session);
        setMessages([]);
        setUnreadCounts((prev) => ({ ...prev, [session.session_id]: 0 }));
        lastMessageCountRef.current[session.session_id] = session.message_count || 0;
        fetchMessages(session.session_id);
        // Connect WebSocket for real-time messages
        if (session.status === 'active' || session.status === 'queued') {
            connectWebSocket(session.session_id);
        }
    };

    // Accept a queued session (GET /live-chat/sessions/<id> auto-assigns agent)
    const handleAcceptSession = async (session) => {
        try {
            const response = await axiosInstance.get(`/live-chat/sessions/${session.session_id}`);
            const sessionData = response.data?.data || {};
            setSnackbar({ open: true, message: 'Session accepted successfully', severity: 'success' });
            fetchSessions();
            const updatedSession = { ...session, status: sessionData.status || 'active' };
            setActiveSession(updatedSession);
            setMessages([]);
            setUnreadCounts((prev) => ({ ...prev, [updatedSession.session_id]: 0 }));
            lastMessageCountRef.current[updatedSession.session_id] = updatedSession.message_count || 0;
            fetchMessages(updatedSession.session_id);

            // Connect WebSocket and wait for it before sending agent_assigned
            await connectWebSocket(updatedSession.session_id);

            // Send agent_assigned system message via WebSocket so chatbot shows agent profile
            const sudoName = sessionData.agent?.sudo_name || user?.name || 'Agent';
            const sudoRole = sessionData.agent?.role_designation || 'Support Agent';
            liveChatSocket.sendAgentAssigned(session.session_id, user?.user_id, sudoName, sudoRole);
        } catch (err) {
            console.error('Error accepting session:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to accept session',
                severity: 'error',
            });
        }
    };

    // Close/resolve active session
    const handleCloseSession = async () => {
        if (!activeSession) return;
        try {
            // Send resolve system message via WebSocket before closing
            liveChatSocket.sendSystem(activeSession.session_id, user?.user_id, {
                event: 'agent_resolved_chat',
                message: 'agent_resolved_chat',
            });
            await axiosInstance.post(`/live-chat/sessions/${activeSession.session_id}/resolve`);
            // Disconnect WebSocket for this session
            if (wsSessionRef.current && wsCallbackRef.current) {
                liveChatSocket.unsubscribe(wsSessionRef.current, wsCallbackRef.current);
                wsSessionRef.current = null;
                wsCallbackRef.current = null;
            }
            setSnackbar({ open: true, message: 'Session resolved', severity: 'success' });
            setActiveSession(null);
            setMessages([]);
            fetchSessions();
        } catch (err) {
            console.error('Error closing session:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to close session',
                severity: 'error',
            });
        }
    };

    // Send message via WebSocket (with REST fallback for DB persistence)
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!messageInput.trim() || !activeSession) return;
        const msg = messageInput.trim();
        setMessageInput('');
        // Optimistically add agent message to UI
        setMessages((prev) => [
            ...prev,
            {
                message_id: `local-${Date.now()}`,
                sender_type: 'agent',
                sender_name: user?.name || 'Agent',
                message: msg,
                created_at: new Date().toISOString(),
            },
        ]);
        // Try WebSocket first (handles DB save + real-time broadcast)
        if (liveChatSocket.isConnected()) {
            liveChatSocket.sendMessage(activeSession.session_id, user?.user_id, msg);
        } else {
            // REST fallback - saves to DB but no real-time broadcast to chatbot
            console.warn('[LiveConversations] WebSocket not connected, falling back to REST');
            try {
                await axiosInstance.post(`/live-chat/sessions/${activeSession.session_id}/message`, {
                    sender_id: user?.user_id,
                    message: msg,
                });
            } catch (err) {
                console.error('Failed to send message via REST fallback:', err);
                setSnackbar({ open: true, message: 'Failed to send message', severity: 'error' });
            }
        }
    };

    // Format time
    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        } catch {
            return '';
        }
    };

    // Get initials for avatar
    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map((w) => w[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    return (
        <Box sx={{ p: { xs: 1, md: 2 }, height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ChatIcon sx={{ color: ACCENT, fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                    Live Conversations
                </Typography>
            </Box>

            {/* Agent Status Bar */}
            <Paper
                elevation={0}
                sx={{
                    mb: 2,
                    px: 2,
                    py: 1.2,
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: agentStatus === 'online' ? `${ACCENT}08` : '#fff',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FiberManualRecordIcon
                        sx={{
                            fontSize: 12,
                            color: agentStatus === 'online' ? ACCENT : agentStatus === 'busy' ? '#f59e0b' : '#94a3b8',
                            animation: agentStatus === 'online' ? 'pulse 2s infinite' : 'none',
                            '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.4 },
                            },
                        }}
                    />
                    <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                        {agentStatus === null
                            ? 'Checking status...'
                            : agentStatus === 'online'
                            ? 'Online - Receiving chats'
                            : agentStatus === 'busy'
                            ? 'Busy'
                            : agentStatus === 'away'
                            ? 'Away'
                            : 'Offline - Go online to receive chats'}
                    </Typography>
                </Box>
                <Chip
                    label={agentStatus === 'online' ? 'Go Offline' : 'Go Online'}
                    icon={<PowerSettingsNewIcon sx={{ fontSize: 16 }} />}
                    onClick={handleToggleStatus}
                    disabled={statusLoading || agentStatus === null}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: 'pointer',
                        bgcolor: agentStatus === 'online' ? '#ef4444' : ACCENT,
                        color: '#fff',
                        '&:hover': { bgcolor: agentStatus === 'online' ? '#dc2626' : ACCENT_DARK },
                        '& .MuiChip-icon': { color: '#fff' },
                    }}
                />
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2, fontFamily }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Main split layout */}
            <Box sx={{ display: 'flex', flex: 1, gap: 2, overflow: 'hidden', minHeight: 0 }}>
                {/* Left Sidebar - Session List */}
                <Paper
                    elevation={0}
                    sx={{
                        width: { xs: activeSession ? 0 : '100%', md: 360 },
                        display: { xs: activeSession ? 'none' : 'flex', md: 'flex' },
                        flexDirection: 'column',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden',
                        flexShrink: 0,
                    }}
                >
                    {/* Search */}
                    <Box sx={{ p: 1.5 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search sessions..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                sx: { fontFamily, borderRadius: '10px', fontSize: 14 },
                            }}
                        />
                    </Box>

                    {/* Filter Tabs */}
                    <Tabs
                        value={filterTab}
                        onChange={(e, v) => setFilterTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 40,
                            px: 1,
                            '& .MuiTab-root': {
                                fontFamily,
                                fontSize: 13,
                                fontWeight: 600,
                                textTransform: 'none',
                                minHeight: 40,
                                py: 0,
                            },
                            '& .Mui-selected': { color: ACCENT },
                            '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                        }}
                    >
                        {FILTER_TABS.map((tab) => (
                            <Tab key={tab} label={tab} />
                        ))}
                    </Tabs>

                    <Divider />

                    {/* Session List */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={32} sx={{ color: ACCENT }} />
                            </Box>
                        ) : filteredSessions.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
                                <InboxIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: 14 }}>
                                    No sessions found
                                </Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {filteredSessions.map((session) => (
                                    <React.Fragment key={session.session_id}>
                                        <ListItem disablePadding>
                                            <ListItemButton
                                                onClick={() => handleSelectSession(session)}
                                                selected={activeSession?.session_id === session.session_id}
                                                sx={{
                                                    py: 1.5,
                                                    px: 2,
                                                    '&.Mui-selected': {
                                                        backgroundColor: `${ACCENT}10`,
                                                        borderRight: `3px solid ${ACCENT}`,
                                                    },
                                                    '&.Mui-selected:hover': {
                                                        backgroundColor: `${ACCENT}18`,
                                                    },
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Badge
                                                        overlap="circular"
                                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                        badgeContent={
                                                            session.status === 'active' ? (
                                                                <Box
                                                                    sx={{
                                                                        width: 10,
                                                                        height: 10,
                                                                        borderRadius: '50%',
                                                                        bgcolor: ACCENT,
                                                                        border: '2px solid #fff',
                                                                    }}
                                                                />
                                                            ) : null
                                                        }
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: session.status === 'queued' ? '#f59e0b' : ACCENT,
                                                                width: 40,
                                                                height: 40,
                                                                fontSize: 14,
                                                                fontFamily,
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {getInitials(session.customer_name)}
                                                        </Avatar>
                                                    </Badge>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily,
                                                                    fontWeight: 600,
                                                                    fontSize: 14,
                                                                    color: '#1e293b',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {session.customer_name || 'Unknown Customer'}
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily,
                                                                    fontSize: 11,
                                                                    color: '#94a3b8',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {formatTime(session.last_message_at || session.created_at)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily,
                                                                    fontSize: 12,
                                                                    color: '#64748b',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    maxWidth: 160,
                                                                }}
                                                            >
                                                                {session.last_message || 'No messages yet'}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {unreadCounts[session.session_id] > 0 && (
                                                                <Box sx={{
                                                                    minWidth: 18,
                                                                    height: 18,
                                                                    borderRadius: '9px',
                                                                    bgcolor: ACCENT,
                                                                    color: '#fff',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: 10,
                                                                    fontWeight: 700,
                                                                    fontFamily,
                                                                    px: 0.5,
                                                                }}>
                                                                    {unreadCounts[session.session_id]}
                                                                </Box>
                                                            )}
                                                            <Chip
                                                                label={STATUS_CONFIG[session.status]?.label || session.status}
                                                                color={STATUS_CONFIG[session.status]?.color || 'default'}
                                                                size="small"
                                                                sx={{
                                                                    fontFamily,
                                                                    fontSize: 10,
                                                                    height: 20,
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                            </Box>
                                                        </Box>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                        <Divider component="li" />
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Box>

                    {/* Refresh Button */}
                    <Divider />
                    <Box sx={{ p: 1, textAlign: 'center' }}>
                        <Tooltip title="Refresh sessions">
                            <IconButton onClick={fetchSessions} size="small" sx={{ color: ACCENT }}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Paper>

                {/* Right Panel - Active Chat */}
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden',
                    }}
                >
                    {!activeSession ? (
                        /* Empty State */
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 4,
                            }}
                        >
                            <ChatIcon sx={{ fontSize: 72, color: '#e2e8f0', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontFamily, fontWeight: 600, color: '#94a3b8', mb: 1 }}>
                                No Conversation Selected
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: 14, color: '#cbd5e1', textAlign: 'center' }}>
                                Select a conversation from the sidebar to start chatting,
                                <br />
                                or accept a queued session to begin.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Chat Header */}
                            <Box
                                sx={{
                                    px: 2.5,
                                    py: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderBottom: '1px solid #e2e8f0',
                                    bgcolor: '#fafbfc',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {/* Back button on mobile */}
                                    <IconButton
                                        size="small"
                                        onClick={() => setActiveSession(null)}
                                        sx={{ display: { xs: 'flex', md: 'none' }, color: '#64748b' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                    <Avatar
                                        sx={{
                                            bgcolor: ACCENT,
                                            width: 36,
                                            height: 36,
                                            fontSize: 14,
                                            fontFamily,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {getInitials(activeSession.customer_name)}
                                    </Avatar>
                                    <Box>
                                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: 15, color: '#1e293b' }}>
                                            {activeSession.customer_name || 'Unknown Customer'}
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: 12, color: '#94a3b8' }}>
                                            {activeSession.customer_email || 'Session'} #{String(activeSession.session_id || '').slice(-6)}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label={STATUS_CONFIG[activeSession.status]?.label || activeSession.status}
                                        color={STATUS_CONFIG[activeSession.status]?.color || 'default'}
                                        size="small"
                                        sx={{ fontFamily, fontWeight: 600, fontSize: 12 }}
                                    />
                                    {activeSession.status === 'queued' && (
                                        <Tooltip title="Accept this session">
                                            <IconButton
                                                onClick={() => handleAcceptSession(activeSession)}
                                                size="small"
                                                sx={{ color: ACCENT }}
                                            >
                                                <CheckCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {(activeSession.status === 'active' || activeSession.status === 'queued') && (
                                        <Tooltip title="Close session">
                                            <IconButton onClick={handleCloseSession} size="small" sx={{ color: '#ef4444' }}>
                                                <CloseIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>

                            {/* Bot History Banner */}
                            {messages.some((m) => (m.sender_type || m.sender || m.role) === 'bot') && (
                                <Box sx={{
                                    px: 2.5,
                                    py: 1,
                                    bgcolor: '#eef2ff',
                                    borderBottom: '1px solid rgba(99,102,241,0.15)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}>
                                    <SmartToyIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                                    <Typography sx={{ fontFamily, fontSize: 12, color: '#4338ca', fontWeight: 500 }}>
                                        This conversation includes prior AI bot messages
                                    </Typography>
                                </Box>
                            )}

                            {/* Messages Area */}
                            <Box
                                sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                    px: 2.5,
                                    py: 2,
                                    bgcolor: '#f8fafc',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                }}
                            >
                                {messagesLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress size={28} sx={{ color: ACCENT }} />
                                    </Box>
                                ) : messages.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <Typography sx={{ fontFamily, fontSize: 14, color: '#94a3b8' }}>
                                            No messages yet. Start the conversation!
                                        </Typography>
                                    </Box>
                                ) : (
                                    messages.map((msg, idx) => {
                                        const senderType = msg.sender_type || msg.sender || msg.role || '';
                                        const isAgent = senderType === 'agent';
                                        const isBot = senderType === 'bot';
                                        const isSystem = senderType === 'system';
                                        const messageText = msg.message || msg.content || msg.text || '';

                                        // Check if this is the transition point from bot history to live chat
                                        const prevMsg = idx > 0 ? messages[idx - 1] : null;
                                        const prevSenderType = prevMsg ? (prevMsg.sender_type || prevMsg.sender || prevMsg.role || '') : '';
                                        const isBotToLiveTransition = !isBot && !isSystem && prevSenderType === 'bot';

                                        // System messages (auto-resolved, session closed, etc.)
                                        if (isSystem) {
                                            return (
                                                <Box key={msg.message_id || idx} sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                                                    <Chip
                                                        icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                                                        label={messageText}
                                                        size="small"
                                                        sx={{
                                                            fontFamily,
                                                            fontSize: 11,
                                                            fontWeight: 500,
                                                            bgcolor: 'rgba(148,163,184,0.12)',
                                                            color: '#64748b',
                                                            height: 26,
                                                            '& .MuiChip-icon': { color: '#94a3b8' },
                                                        }}
                                                    />
                                                </Box>
                                            );
                                        }

                                        const isOutgoing = isAgent;
                                        const avatarBg = isBot ? '#6366f1' : isAgent ? ACCENT_DARK : '#94a3b8';
                                        const bubbleBg = isBot ? '#eef2ff' : isAgent ? ACCENT : '#e2e8f0';
                                        const bubbleColor = isAgent ? '#fff' : '#1e293b';

                                        return (
                                            <React.Fragment key={msg.message_id || idx}>
                                                {/* Bot-to-live transition marker */}
                                                {isBotToLiveTransition && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
                                                        <Divider sx={{ flex: 1 }} />
                                                        <Chip
                                                            icon={<SupportAgentIcon sx={{ fontSize: 14 }} />}
                                                            label="Live Agent Joined"
                                                            size="small"
                                                            sx={{
                                                                fontFamily,
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                bgcolor: `${ACCENT}14`,
                                                                color: ACCENT_DARK,
                                                                border: `1px solid ${ACCENT}30`,
                                                                '& .MuiChip-icon': { color: ACCENT },
                                                            }}
                                                        />
                                                        <Divider sx={{ flex: 1 }} />
                                                    </Box>
                                                )}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, maxWidth: '70%' }}>
                                                        {!isOutgoing && (
                                                            <Tooltip title={isBot ? 'AI Bot' : (msg.sender_name || 'Customer')}>
                                                                <Avatar sx={{ width: 28, height: 28, bgcolor: avatarBg, fontSize: 12 }}>
                                                                    {isBot ? <SmartToyIcon sx={{ fontSize: 16 }} /> : <PersonIcon sx={{ fontSize: 16 }} />}
                                                                </Avatar>
                                                            </Tooltip>
                                                        )}
                                                        <Box>
                                                            {/* Sender label for bot messages */}
                                                            {isBot && (
                                                                <Typography sx={{ fontFamily, fontSize: 10, color: '#6366f1', fontWeight: 600, mb: 0.3, px: 0.5 }}>
                                                                    AI Bot
                                                                </Typography>
                                                            )}
                                                            <Box
                                                                sx={{
                                                                    px: 2,
                                                                    py: 1.2,
                                                                    borderRadius: isOutgoing
                                                                        ? '16px 16px 4px 16px'
                                                                        : '16px 16px 16px 4px',
                                                                    bgcolor: bubbleBg,
                                                                    color: bubbleColor,
                                                                    ...(isBot && { border: '1px solid rgba(99,102,241,0.15)' }),
                                                                }}
                                                            >
                                                                {isBot ? (
                                                                    <Box
                                                                        sx={{
                                                                            fontFamily,
                                                                            fontSize: 14,
                                                                            lineHeight: 1.6,
                                                                            wordBreak: 'break-word',
                                                                            '& pre': { my: 1 },
                                                                            '& a': { color: '#3b82f6' },
                                                                            '& li': { ml: 2 },
                                                                        }}
                                                                        dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(messageText) }}
                                                                    />
                                                                ) : (
                                                                    <Typography
                                                                        sx={{ fontFamily, fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word' }}
                                                                    >
                                                                        {messageText}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily,
                                                                    fontSize: 10,
                                                                    color: '#94a3b8',
                                                                    mt: 0.3,
                                                                    textAlign: isOutgoing ? 'right' : 'left',
                                                                    px: 0.5,
                                                                }}
                                                            >
                                                                {formatTime(msg.created_at || msg.timestamp)}
                                                            </Typography>
                                                        </Box>
                                                        {isOutgoing && (
                                                            <Avatar sx={{ width: 28, height: 28, bgcolor: ACCENT_DARK, fontSize: 12 }}>
                                                                <SupportAgentIcon sx={{ fontSize: 16 }} />
                                                            </Avatar>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </React.Fragment>
                                        );
                                    })
                                )}
                                <div ref={messagesEndRef} />
                            </Box>

                            {/* Message Input */}
                            {(activeSession.status === 'active') && (
                                <Box
                                    component="form"
                                    onSubmit={handleSendMessage}
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        borderTop: '1px solid #e2e8f0',
                                        bgcolor: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Type a message..."
                                        value={messageInput}
                                        onChange={(e) => setMessageInput(e.target.value)}
                                        disabled={sendingMessage}
                                        autoComplete="off"
                                        InputProps={{
                                            sx: {
                                                fontFamily,
                                                borderRadius: '12px',
                                                fontSize: 14,
                                                bgcolor: '#f8fafc',
                                            },
                                        }}
                                    />
                                    <IconButton
                                        type="submit"
                                        disabled={!messageInput.trim() || sendingMessage}
                                        sx={{
                                            bgcolor: ACCENT,
                                            color: '#fff',
                                            width: 40,
                                            height: 40,
                                            '&:hover': { bgcolor: ACCENT_DARK },
                                            '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
                                        }}
                                    >
                                        {sendingMessage ? (
                                            <CircularProgress size={18} sx={{ color: '#fff' }} />
                                        ) : (
                                            <SendIcon sx={{ fontSize: 20 }} />
                                        )}
                                    </IconButton>
                                </Box>
                            )}

                            {/* Accept prompt for queued sessions */}
                            {activeSession.status === 'queued' && (
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 2,
                                        borderTop: '1px solid #e2e8f0',
                                        bgcolor: '#fffbeb',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography sx={{ fontFamily, fontSize: 14, color: '#92400e', mb: 1 }}>
                                        This session is waiting to be accepted.
                                    </Typography>
                                    <Chip
                                        label="Accept Session"
                                        icon={<CheckCircleIcon />}
                                        onClick={() => handleAcceptSession(activeSession)}
                                        sx={{
                                            fontFamily,
                                            fontWeight: 600,
                                            bgcolor: ACCENT,
                                            color: '#fff',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: ACCENT_DARK },
                                            '& .MuiChip-icon': { color: '#fff' },
                                        }}
                                    />
                                </Box>
                            )}

                            {/* Closed / resolved notice */}
                            {(activeSession.status === 'closed' || activeSession.status === 'resolved') && (
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        borderTop: '1px solid #e2e8f0',
                                        bgcolor: '#f1f5f9',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography sx={{ fontFamily, fontSize: 13, color: '#64748b' }}>
                                        This session has been {activeSession.status}. No further messages can be sent.
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Paper>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ fontFamily }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
